// src/pages/Wishlist.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { productRepository } from "../repositories/productRepository";
import { wishlistRepository } from "../repositories/wishlistRepository";
import { useAuth } from "../hooks/useAuth";
import { Button } from "../components/ui";
import { errorDev } from '../utils/logger.js';

export default function Wishlist() {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();
  const userId = user?.uid;

  useEffect(() => {
    if (!userId) {
      setWishlistItems([]);
      setLoading(false);
      return;
    }

    let cancelled = false;

    const mergeByProductId = (rows) => {
      const map = new Map();
      rows.forEach((item) => {
        if (!item?.productId) return;
        if (!map.has(item.productId)) map.set(item.productId, item);
      });
      return Array.from(map.values());
    };

    const hydrateProducts = async (entries) => {
      const products = await productRepository.getByIds(entries.map((entry) => entry.productId));
      const productMap = new Map(products.map((product) => [product.id, product]));

      return entries
        .map((entry) => ({
          wishlistId: entry.id,
          collection: entry.collection,
          product: productMap.get(entry.productId) || null,
        }))
        .filter((entry) => entry.product);
    };

    const refresh = async (rows = []) => {
      try {
        const unique = mergeByProductId(rows);
        const hydrated = await hydrateProducts(unique);
        if (!cancelled) {
          setWishlistItems(hydrated);
          setLoading(false);
        }
      } catch (err) {
        errorDev("Operation failed:", err);
        if (!cancelled) setLoading(false);
      }
    };

    const unsubscribe = wishlistRepository.subscribeAll(userId, refresh, (error) => {
      errorDev("Wishlist subscription failed:", error);
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [userId]);

  const handleRemove = async (entry) => {
    if (!entry?.wishlistId) return;
    if (!window.confirm("Remove this product from your wishlist?")) return;

    await wishlistRepository.removeWithLegacyFallback(userId, entry.wishlistId);
  };

  if (!userId) return <div className="p-6">Please login to see your wishlist.</div>;
  if (loading) return <div className="p-6">Loading wishlist...</div>;

  return (
    <div className="max-w-5xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">My Wishlist</h1>
      {wishlistItems.length === 0 ? (
        <p>You have no items in your wishlist.</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
          {wishlistItems.map(({ wishlistId, product, collection }) => (
            <div
              key={`${collection}-${wishlistId}`}
              className="border rounded p-3 cursor-pointer hover:shadow-lg transition"
              onClick={() => navigate(`/product/${product.id}`)}
              title="Click to view product details"
            >
              {product.images?.[0] ? (
                <img
                  src={product.images[0]}
                  alt={product.title}
                  className="w-full h-40 object-cover rounded mb-2"
                  loading="lazy"
                />
              ) : (
                <div className="w-full h-40 bg-gray-200 rounded mb-2 flex items-center justify-center text-gray-500">
                  No image
                </div>
              )}
              <h3 className="font-semibold">{product.title}</h3>
              <p className="text-green-700 font-bold">{product.price} RWF</p>
              <Button
                variant="danger"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove({ wishlistId, collection });
                }}
              >
                Remove from Wishlist
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
